import React, { useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Table } from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchBillById,
  fetchCustomersList,
  fetchSessionsList,
  fetchSuppliersList,
  fetchTripsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { fetchTaxList } from "../../../slices/TaxMaster/thunk";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

const validationSchema = Yup.object({
  customerId: Yup.string().required("Customer is required"),
  billDate: Yup.date().required("Bill date is required"),
  tripIds: Yup.array().min(1, "Select at least one trip"),
  // selectedTaxId: Yup.string().required("Please select tax"),
  sessionId: Yup.string().required("Please select session"),
});

const EditBillForm = ({ billId, onSubmit, onCloseForm }) => {
  const dispatch = useDispatch();
  const [chargesInitialized, setChargesInitialized] = useState(false);

  const [totals, setTotals] = useState({
    subTotal: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGst: 0,
    toll: 0,
    handling: 0,
    other: 0,
    extraCharges: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      trips: state.Trip.trips,
      loading: state.Trip.loading,
      taxes: state.TaxMaster.taxes,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,
      session: state.Session.sessions,
      billDetails: state.SalesInvoice.selectedBill,
      suppliers: state.Supplier.suppliers,
      activeSessions: state.Session.selectedSessionYear,
    })
  );

  const {
    trips,
    loading,
    taxes,
    customersList,
    session,
    billDetails,
    vehicles,
    suppliers,
    activeSessions,
  } = useSelector(selector);

  console.group("this is active sesstion", activeSessions);

  // fetch static lists and bill details
  useEffect(() => {
    if (!billId) return;
    dispatch(fetchTaxList({ isPagination: 0 }));
    dispatch(fetchVehiclesList({ isPagination: 0 }));
    dispatch(fetchCustomersList({ isPagination: 0 }));
    dispatch(fetchSessionsList({ isPagination: 0 }));
    dispatch(fetchSuppliersList({ isPagination: 0 }));
    dispatch(fetchBillById(billId));
  }, [dispatch, billId]);

  // when bill details arrive, load trips for that customer
  useEffect(() => {
    if (billDetails?.customerId) {
      dispatch(
        fetchTripsList({
          customerId: billDetails?.customerId,
          sessionId: billDetails?.sessionId,
          vehicleId: billDetails?.billInvoiceResponses?.[0]?.vehicleId,
          tripType: mapPartyTypeToTripType(billDetails?.partyType),
          isPagination: 0,
        })
      );
    }
  }, [dispatch, billDetails?.customerId]);

  // helper: convert dd-mm-yyyy -> Date safely
  const convertDate = (d) => {
    if (!d) return new Date();
    if (d instanceof Date && !isNaN(d)) return d;
    const parts = String(d).split("-");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const dt = new Date(`${year}-${month}-${day}`);
      return isNaN(dt) ? new Date() : dt;
    }
    const dt2 = new Date(d);
    return isNaN(dt2) ? new Date() : dt2;
  };

  // calculate totals:
  // - GST applies only on subTotal (sum of trip.amount)
  // - extra charges (toll/handling/other) are added to grandTotal WITHOUT GST
  const calculateTotals = (
    selectedTripIds = [],
    selectedTaxId = "",
    includeCharges = false
  ) => {
    const normalized = (selectedTripIds || []).map((id) => String(id));
    const selectedTrips = (trips || []).filter((t) =>
      normalized.includes(String(t.id))
    );

    const subTotal = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    );

    // sum extra charges from selected trips (only if includeCharges true)
    let toll = 0,
      handling = 0,
      other = 0;

    if (includeCharges) {
      toll = selectedTrips.reduce(
        (s, t) => s + (Number(t.tollCharges) || 0),
        0
      );
      handling = selectedTrips.reduce(
        (s, t) => s + (Number(t.handlingCharges) || 0),
        0
      );
      other = selectedTrips.reduce(
        (s, t) => s + (Number(t.otherCharges) || 0),
        0
      );
    }

    const extraCharges = toll + handling + other;

    const selectedTax = (taxes || []).find((t) => t.id === selectedTaxId);

    let cgst = 0,
      sgst = 0,
      igst = 0;

    if (selectedTax) {
      if (selectedTax.taxType === "C&SGST") {
        cgst = (subTotal * Number(selectedTax.cgst || 0)) / 100;
        sgst = (subTotal * Number(selectedTax.sgst || 0)) / 100;
      } else if (selectedTax.taxType === "IGST") {
        igst = (subTotal * Number(selectedTax.taxPerc || 0)) / 100;
      }
    }

    const totalGst = cgst + sgst + igst;
    const grandTotal = subTotal + totalGst + extraCharges;

    setTotals({
      subTotal,
      cgst,
      sgst,
      igst,
      totalGst,
      toll,
      handling,
      other,
      extraCharges,
      grandTotal,
    });
  };

  const tripOptions = [
    { value: "KM", label: "OutStation" },
    { value: "perTrip", label: "Local" },
  ];

  const taxOptions = (taxList) =>
    (taxList || []).map((t) => ({
      label: `${t.name?.toUpperCase()} (${t.taxType})`,
      value: t.id,
    }));

  const mapPartyTypeToTripType = (partyType) => {
    if (partyType === "OUTSTATION") return "KM";
    if (partyType === "LOCAL") return "perTrip";
    return "";
  };

  // prepare safe initial tripIds from billDetails
  const initialTripIdsFromBill = (bd) => {
    if (!bd) return [];
    const arr = bd.billInvoiceResponses || bd.billInvoiceResponse || [];
    return arr.map((x) => String(x.dailyTripId));
  };

  // prepare initial session id
  const initialSessionId = (bd) => bd?.sessionId || "";

  // determine default includeCharges based on billDetails existing charges
  const defaultIncludeCharges = Boolean(
    (billDetails?.tollCharges || 0) > 0 ||
      (billDetails?.handlingCharges || 0) > 0 ||
      (billDetails?.otherCharges || 0) > 0
  );

  return (
    <div className="p-3">
      <h4 className="mb-3">Edit Bill</h4>

      <Formik
        enableReinitialize
        initialValues={{
          customerId: billDetails?.customerId || "",
          customerAddress: billDetails?.customerAddress || "",
          customerGSTNo: billDetails?.customerGSTNo || "",
          billDate: convertDate(billDetails?.billDate),
          tripIds: initialTripIdsFromBill(billDetails),
          selectedTaxId:
            billDetails?.taxId || billDetails?.selectedTaxId || null,
          sessionId: initialSessionId(billDetails),
          vehicleId: billDetails?.billInvoiceResponses?.[0]?.vehicleId || "",
          // keep initial tax fields for reference (not used for calc)
          cgst: billDetails?.cgst,
          sgst: billDetails?.sgst,
          igst: billDetails?.igst,
          totalGst: billDetails?.totalGst,
          includeCharges: defaultIncludeCharges,
          tripType: mapPartyTypeToTripType(billDetails?.partyType),
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const selectedTripsData = trips.filter((t) =>
            values.tripIds.includes(t.id)
          );
          const totalKmTripRate = selectedTripsData[0]?.rate || 0;

          const totalKmTrip = selectedTripsData.reduce(
            (sum, t) => sum + (Number(t.kmOrTrip) || 0),
            0
          );

          const d = values.billDate;
          const formattedBillDate =
            ("0" + d.getDate()).slice(-2) +
            "-" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "-" +
            d.getFullYear();

          const id = billDetails?.id;
          const payload = {
            customerId: values.customerId,
            customerAddress: values.customerAddress || "",
            customerGSTNo: values.customerGSTNo || "",
            billDate: formattedBillDate,
            sessionId: values.sessionId || billDetails?.sessionId,
            // totals: send computed totals (API may expect different keys)
            totalAmount: totals.subTotal,
            cgst: totals.cgst,
            sgst: totals.sgst,
            igst: totals.igst,
            totalGst: totals.totalGst,
            subTotal: totals.subTotal,
            // include individual charges if includeCharges true else zeros
            tollCharges: values.includeCharges ? totals.toll : 0,
            handlingCharges: values.includeCharges ? totals.handling : 0,
            otherCharges: values.includeCharges ? totals.other : 0,
            grandTotal: totals.grandTotal,
            kmOrTrip: totalKmTrip || 0,
            rate: totalKmTripRate || 0,
            billInvoiceRequest: (values.tripIds || []).map((id) => ({
              dailyTripId: id,
              id: id,
            })),
            taxId: values.selectedTaxId,
            includeCharges: Boolean(values.includeCharges),
          };

          if (onSubmit) onSubmit(id, payload);
          if (onCloseForm) onCloseForm();
        }}
      >
        {({ values, setFieldValue, setFieldTouched }) => {
          // ensure we calculate totals on initial load and whenever relevant data arrives or user toggles includeCharges
          useEffect(() => {
            // Wait until bill details, taxes and trips are available
            if (
              !billDetails ||
              (taxes?.length === 0 && !billDetails?.taxId) ||
              trips?.length === 0
            )
              return;

            // determine taxId: prefer billDetails.taxId/selectedTaxId, otherwise fallback based on cgst/igst values
            let taxId =
              billDetails?.selectedTaxId ||
              billDetails?.taxId ||
              values.selectedTaxId ||
              "";

            if (!taxId) {
              if (
                (billDetails?.cgst || 0) > 0 ||
                (billDetails?.sgst || 0) > 0
              ) {
                const t = taxes.find((x) => x.taxType === "C&SGST");
                taxId = t?.id || "";
              } else if ((billDetails?.igst || 0) > 0) {
                const t = taxes.find((x) => x.taxType === "IGST");
                taxId = t?.id || "";
              }
            }

            if (taxId && values.selectedTaxId !== taxId) {
              setFieldValue("selectedTaxId", taxId);
            }

            const initialTripIds = initialTripIdsFromBill(billDetails);
            if (
              (!values.tripIds || values.tripIds.length === 0) &&
              initialTripIds.length > 0
            ) {
              setFieldValue("tripIds", initialTripIds);
            }

            // if (values.includeCharges !== defaultIncludeCharges) {
            //   setFieldValue("includeCharges", defaultIncludeCharges);
            // }

            // run only on first load
            if (!chargesInitialized) {
              setFieldValue("includeCharges", defaultIncludeCharges);
              setChargesInitialized(true);
            }

            // compute totals using either Formik tripIds (if present) or initialTripIds
            const tripIdsToUse =
              Array.isArray(values.tripIds) && values.tripIds.length > 0
                ? values.tripIds
                : initialTripIds;

            calculateTotals(
              tripIdsToUse,
              taxId,
              Boolean(values.includeCharges)
            );
            // depend on billDetails,taxes,trips and values.includeCharges & selectedTaxId (we read them above)
          }, [
            billDetails,
            taxes,
            trips,
            values.includeCharges,
            values.selectedTaxId,
          ]);

          const handleTripToggle = (tripId, checked) => {
            const id = String(tripId);
            const current = Array.isArray(values.tripIds)
              ? [...values.tripIds]
              : [];
            const updated = checked
              ? [...new Set([...current, id])]
              : current.filter((t) => t !== id);
            setFieldValue("tripIds", updated);
            calculateTotals(
              updated,
              values.selectedTaxId,
              Boolean(values.includeCharges)
            );
          };

          const handleTaxChange = (opt) => {
            const taxId = opt?.value || "";
            setFieldValue("selectedTaxId", taxId);
            calculateTotals(
              values.tripIds || initialTripIdsFromBill(billDetails),
              taxId,
              Boolean(values.includeCharges)
            );
          };

          const clearTrips = () => {
            setFieldValue("tripIds", []);
            calculateTotals(
              [],
              values.selectedTaxId,
              Boolean(values.includeCharges)
            );
          };

          return (
            <Form>
              <Row className="g-4">
                {/* BILL DATE / SESSION */}
                <Col md={3}>
                  <div>
                    <label className="form-label">
                      Session <span className="text-danger">*</span>
                    </label>
                    {activeSessions &&
                      values.sessionId !== activeSessions?.id &&
                      setFieldValue("sessionId", activeSessions?.id)}
                    <input
                      type="text"
                      className="form-control"
                      value={activeSessions?.name || ""}
                      disabled
                    />
                    <input
                      type="hidden"
                      name="sessionId"
                      value={values.sessionId}
                    />
                  </div>
                </Col>

                {/* CUSTOMER */}
                <Col md={3}>
                  <Label>Customer *</Label>
                  <Select
                    options={customersList.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label:
                              customersList.find(
                                (c) => c.id === values.customerId
                              )?.name || "",
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("customerId", opt?.value || "");
                      clearTrips();
                      dispatch(
                        fetchTripsList({
                          customerId: opt?.value,

                          isBill: false,
                        })
                      );
                      // Clear selected trips and totals
                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        Boolean(values.includeCharges)
                      );
                    }}
                  />
                </Col>

                {/* VEHICLE */}
                <Col md={3}>
                  <Label>
                    Vehicle <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={vehicles.map((v) => ({
                      value: v.id,
                      label: v.vehicleNo,
                    }))}
                    value={
                      values.vehicleId
                        ? {
                            value: values.vehicleId,
                            label:
                              vehicles.find((v) => v.id === values.vehicleId)
                                ?.vehicleNo || "",
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("vehicleId", opt?.value || "");
                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: opt?.value,
                          sessionId: values?.sessionId,

                          isBill: false,
                        })
                      );
                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        Boolean(values.includeCharges)
                      );
                    }}
                  />
                </Col>

                <Col md={3}>
                  <Label>
                    Trip Type <span className="text-danger">*</span>
                  </Label>

                  <Select
                    options={tripOptions}
                    value={
                      tripOptions.find(
                        (item) => item.value === values.tripType
                      ) || null
                    }
                    onChange={(opt) => {
                      setFieldValue("tripType", opt?.value);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId: values.supplierId,
                          tripType: opt.value,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        values.includeCharges
                      );
                    }}
                    onBlur={() => setFieldTouched("tripType", true)}
                    placeholder="Select Trip Type"
                  />
                </Col>

                {/* SUPPLIER */}
                <Col md={3}>
                  <Label>Supplier</Label>
                  <Select
                    options={suppliers?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const supplierId = opt ? opt.value : null;
                      setFieldValue("supplierId", supplierId);
                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId,
                          sessionId: values?.sessionId,
                          isBill: false,
                        })
                      );
                      setFieldValue("tripIds", []);
                      calculateTotals(
                        [],
                        values.selectedTaxId,
                        Boolean(values.includeCharges)
                      );
                    }}
                    placeholder="Select Supplier"
                    isClearable
                  />
                </Col>

                {/* BILL DATE */}
                <Col md={3}>
                  <Label>Bill Date *</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      value={values.billDate}
                      onChange={(dates) => setFieldValue("billDate", dates[0])}
                      options={{ dateFormat: "d-m-Y" }}
                    />
                    <ErrorMessage
                      name="billDate"
                      component="div"
                      className="text-danger small"
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                {/* FROM / TO filters */}
                <Col md={3}>
                  <Label>From Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select From Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.fromDate}
                      onChange={(date) => {
                        const fromDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;
                        setFieldValue("fromDate", fromDateValue);
                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: fromDateValue,
                            toDate: values.toDate,

                            isBill: false,
                          })
                        );
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>To Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select To Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.toDate}
                      onChange={(date) => {
                        const toDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;
                        setFieldValue("toDate", toDateValue);
                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: values.fromDate,
                            toDate: toDateValue,

                            isBill: false,
                          })
                        );
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                {/* TRIP TABLE */}
                <Col md={12}>
                  <Label>Select Trip(s) *</Label>
                  <Table bordered className="mt-2">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="8" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}

                      {!loading && trips.length === 0 && (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            <i className="ri-emotion-unhappy-line fs-2 d-block mb-1" />
                            No Record found.
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        trips.map((trip) => {
                          const checked = (values.tripIds || [])
                            .map(String)
                            .includes(String(trip.id));
                          return (
                            <tr key={trip.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    handleTripToggle(trip.id, e.target.checked)
                                  }
                                />
                              </td>
                              <td>{trip.customerName}</td>
                              <td>{trip.suppliersName}</td>
                              <td>{trip.tripDate}</td>
                              <td>{trip.vehicleNo}</td>
                              <td>{trip.rate}</td>
                              <td>{trip.kmOrTrip}</td>
                              <td>{trip.amount}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                  <ErrorMessage
                    name="tripIds"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                {/* TAX + SUMMARY */}
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Bill Summary</h5>

                    <div className="form-check mt-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="includeCharges"
                        checked={Boolean(values.includeCharges)}
                        onChange={(e) => {
                          setFieldValue("includeCharges", e.target.checked);
                          calculateTotals(
                            values.tripIds ||
                              initialTripIdsFromBill(billDetails),
                            values.selectedTaxId,
                            e.target.checked
                          );
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="includeCharges"
                      >
                        Include Toll / Handling / Other Charges
                      </Label>
                    </div>

                    <div style={{ width: "320px" }}>
                      <Label>Tax *</Label>
                      <Select
                        options={taxOptions(taxes)}
                        value={
                          values.selectedTaxId
                            ? {
                                value: values.selectedTaxId,
                                label:
                                  (taxes.find(
                                    (t) => t.id === values.selectedTaxId
                                  )?.name || "") +
                                  ` (${
                                    taxes.find(
                                      (t) => t.id === values.selectedTaxId
                                    )?.taxType || ""
                                  })`,
                              }
                            : null
                        }
                        onChange={handleTaxChange}
                        placeholder="Select Tax"
                      />
                    </div>
                  </div>

                  {/* SUMMARY TABLE with charges & GST as required */}
                  <Table bordered className="mt-2">
                    <thead>
                      <tr>
                        <th>Sub Total</th>
                        {(() => {
                          const tax = taxes.find(
                            (t) => t.id === values.selectedTaxId
                          );
                          if (tax?.taxType === "C&SGST")
                            return (
                              <>
                                <th>CGST ({tax.cgst}%)</th>
                                <th>SGST ({tax.sgst}%)</th>
                              </>
                            );
                          if (tax?.taxType === "IGST")
                            return <th>IGST ({tax.taxPerc}%)</th>;
                          return null;
                        })()}
                        {/* charges columns */}
                        {values.includeCharges && <th>Toll</th>}
                        {values.includeCharges && <th>Handling</th>}
                        {values.includeCharges && <th>Other</th>}
                        {values.includeCharges && <th>Extra Total</th>}
                        <th>Total GST</th>
                        <th>Grand Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>₹ {Number(totals.subTotal || 0).toFixed(2)}</td>

                        {(() => {
                          const tax = taxes.find(
                            (t) => t.id === values.selectedTaxId
                          );
                          if (tax?.taxType === "C&SGST")
                            return (
                              <>
                                <td>₹ {Number(totals.cgst || 0).toFixed(2)}</td>
                                <td>₹ {Number(totals.sgst || 0).toFixed(2)}</td>
                              </>
                            );
                          if (tax?.taxType === "IGST")
                            return (
                              <td>₹ {Number(totals.igst || 0).toFixed(2)}</td>
                            );
                          return null;
                        })()}

                        {values.includeCharges && (
                          <td>₹ {Number(totals.toll || 0).toFixed(2)}</td>
                        )}
                        {values.includeCharges && (
                          <td>₹ {Number(totals.handling || 0).toFixed(2)}</td>
                        )}
                        {values.includeCharges && (
                          <td>₹ {Number(totals.other || 0).toFixed(2)}</td>
                        )}
                        {values.includeCharges && (
                          <td>
                            ₹ {Number(totals.extraCharges || 0).toFixed(2)}
                          </td>
                        )}

                        <td>₹ {Number(totals.totalGst || 0).toFixed(2)}</td>
                        <td>
                          <b>₹ {Number(totals.grandTotal || 0).toFixed(2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* SUBMIT */}
                <Col md={12} className="text-end">
                  <Button color="success" className="btn-sm" type="submit">
                    Update Bill
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EditBillForm;
